import { Component } from '@theme/component';

/**
 * @typedef {Object} RoomVariant
 * @property {number} id
 * @property {boolean} [available]
 * @property {string[]} [options]
 * @property {boolean} [hidden]
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {string} [image]
 * @property {string} [meta_image]
 */

/**
 * @typedef {Object} ProductData
 * @property {RoomVariant[]} variants
 */

/**
 * @typedef {Object} VariantSelection
 * @property {string} variantId
 * @property {number} price
 * @property {number} comparePrice
 * @property {string} image
 * @property {string} metaImage
 */

/** @extends {Component<Record<string, never>>} */
export class RoomVariantSelectorComponent extends Component {
    /** @type {AbortController|null} */
    #abortController = null;

    /** @type {ProductData|null} */
    #productData = null;

    connectedCallback() {
        super.connectedCallback();
        this.#init();
    }

    updatedCallback() {
        super.updatedCallback();
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        this.#init();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
    }

    #init() {
        const variantsScript = this.querySelector('.room-info__variants-data');
        if (!variantsScript?.textContent) return;

        try {
            this.#productData = {
                variants: JSON.parse(variantsScript.textContent),
            };
        } catch {
            return;
        }

        if (!this.#abortController) this.#abortController = new AbortController();
        this.#setupVariantInputs();

        if (this.#isMultiOption()) {
            this.#initMultiOption();
            return;
        }

        this.#applyAvailability();
        this.#selectInitialVariant();
    }

    /** @returns {HTMLElement[]} */
    get #optionGroups() {
        return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-option-position]')]);
    }

    #isMultiOption() {
        return this.#optionGroups.length > 1 && Array.isArray(this.#productData?.variants[0]?.options);
    }

    #applyAvailability() {
        const variants = this.#productData?.variants;
        if (!variants) return;

        const availabilityById = new Map(variants.map((variant) => [String(variant.id), variant.available !== false]));

        this.querySelectorAll('.room-info__swatch-input').forEach((input) => {
            const swatchInput = /** @type {HTMLInputElement} */ (input);
            const variantId = swatchInput.dataset.variantId || '';
            const isUnavailable = availabilityById.has(variantId) && availabilityById.get(variantId) === false;
            this.#setInputDisabled(swatchInput, isUnavailable);
        });
    }

    /**
     * @param {HTMLInputElement} input
     * @param {boolean} isUnavailable
     */
    #setInputDisabled(input, isUnavailable) {
        input.disabled = isUnavailable;
        input.setAttribute('aria-disabled', String(isUnavailable));

        const label = input.nextElementSibling;
        if (!(label instanceof HTMLElement)) return;
        let soldOut = label.querySelector('[data-sold-out-label]');
        if (isUnavailable) {
            if (!soldOut) {
                soldOut = document.createElement('span');
                soldOut.className = 'visually-hidden';
                soldOut.setAttribute('data-sold-out-label', '');
                soldOut.textContent = this.dataset.soldOutText || '';
                label.append(soldOut);
            }
        } else {
            soldOut?.remove();
        }
    }

    #setupVariantInputs() {
        this.querySelectorAll('.room-info__swatch-input').forEach((input) => {
            input.addEventListener('change', this.#handleVariantChange, {
                signal: this.#abortController?.signal,
            });
        });
    }

    #selectInitialVariant() {
        const variantToShow = this.dataset.variantToShow;
        let targetInput = null;

        /**
         * @param {Element | null} input
         * @returns {input is HTMLInputElement}
         */
        const isSelectable = (input) =>
            input instanceof HTMLInputElement && !input.disabled && input.getAttribute('aria-disabled') !== 'true';

        const checked = this.querySelector('.room-info__swatch-input:checked');
        if (isSelectable(checked)) targetInput = checked;

        if (!targetInput && variantToShow && variantToShow.trim()) {
            const wanted = variantToShow.toLowerCase().trim();
            for (const input of this.querySelectorAll('.room-info__swatch-input')) {
                if (
                    input instanceof HTMLInputElement &&
                    input.value.toLowerCase().trim() === wanted &&
                    isSelectable(input)
                ) {
                    targetInput = input;
                    break;
                }
            }
        }

        if (!targetInput) {
            for (const input of this.querySelectorAll('.room-info__swatch-input')) {
                if (isSelectable(input)) {
                    targetInput = input;
                    break;
                }
            }
        }

        if (targetInput && !targetInput.checked) {
            targetInput.checked = true;
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    #initMultiOption() {
        const variants = this.#selectableVariants();
        const checkedValues = this.#checkedValues();
        const selectedId = this.closest('.room-info__product')?.getAttribute('data-selected-variant');

        let initial =
            variants.find((variant) => String(variant.id) === selectedId) ||
            variants.find((variant) =>
                checkedValues.every((value, index) => value === null || variant.options?.[index] === value)
            );

        if (!initial || initial.available === false) {
            const wanted = (this.dataset.variantToShow || '').toLowerCase().trim();
            const available = variants.filter((variant) => variant.available !== false);
            /** @param {RoomVariant} variant */
            const matchesWanted = (variant) =>
                [variant.options?.join(' / ') || '', ...(variant.options || [])].some(
                    (value) => value.toLowerCase().trim() === wanted
                );
            initial = available.find(matchesWanted) || available[0] || initial;
        }
        if (!initial) return;

        const changed = String(initial.id) !== selectedId;
        this.#syncOptionGroups(initial);
        if (changed) this.#applySelection(this.#selectionFromVariant(initial));
    }

    /** @returns {RoomVariant[]} */
    #selectableVariants() {
        return (this.#productData?.variants || []).filter(
            (variant) => !variant.hidden && Array.isArray(variant.options)
        );
    }

    /** @returns {(string|null)[]} */
    #checkedValues() {
        const size = this.#productData?.variants[0]?.options?.length || 0;
        /** @type {(string|null)[]} */
        const values = Array.from({ length: size }, () => null);
        this.#optionGroups.forEach((group) => {
            const index = Number(group.dataset.optionPosition) - 1;
            const checked = group.querySelector('.room-info__swatch-input:checked');
            if (checked instanceof HTMLInputElement && index < size) values[index] = checked.value;
        });
        return values;
    }

    /**
     * Picks the variant for the changed value, keeping as many of the other selected values as possible.
     * @param {number} index
     * @param {string} value
     * @returns {RoomVariant|null}
     */
    #resolveVariant(index, value) {
        const selected = this.#checkedValues();
        const candidates = this.#selectableVariants().filter((variant) => variant.options?.[index] === value);
        if (!candidates.length) return null;

        /** @param {RoomVariant} variant */
        const score = (variant) =>
            (variant.available !== false ? 100 : 0) +
            selected.reduce((total, current, position) => total + Number(variant.options?.[position] === current), 0);

        return candidates.reduce((best, candidate) => (score(candidate) > score(best) ? candidate : best));
    }

    /** @param {RoomVariant} variant */
    #syncOptionGroups(variant) {
        const variants = this.#selectableVariants();
        const groups = this.#optionGroups;

        groups.forEach((group, groupIndex) => {
            const index = Number(group.dataset.optionPosition) - 1;
            const upstream = groups.slice(0, groupIndex).map((previous) => Number(previous.dataset.optionPosition) - 1);
            let offeredCount = 0;

            group.querySelectorAll('.room-info__swatch-input').forEach((element) => {
                const input = /** @type {HTMLInputElement} */ (element);
                const matches = variants.filter(
                    (candidate) =>
                        candidate.options?.[index] === input.value &&
                        upstream.every((key) => candidate.options?.[key] === variant.options?.[key])
                );
                const option = input.closest('.room-info__swatch-option');

                if (groupIndex > 0 && option instanceof HTMLElement) option.hidden = matches.length === 0;
                if (matches.length) offeredCount++;

                input.checked = input.value === variant.options?.[index];
                this.#setInputDisabled(input, !matches.some((candidate) => candidate.available !== false));
            });

            if (groupIndex > 0) group.hidden = offeredCount < 2;
            this.#updateValueDisplay(group, variant.options?.[index] || '');
        });
    }

    /**
     * @param {RoomVariant} variant
     * @returns {VariantSelection}
     */
    #selectionFromVariant(variant) {
        return {
            variantId: String(variant.id),
            price: variant.price || 0,
            comparePrice: variant.compare_at_price || 0,
            image: variant.image || '',
            metaImage: variant.meta_image || '',
        };
    }

    /**
     * @param {Element} group
     * @param {string} value
     */
    #updateValueDisplay(group, value) {
        const typeGroups = group.querySelectorAll('.room-info__custom-swatch-group');
        if (typeGroups.length) {
            typeGroups.forEach((typeGroup) => {
                const display = typeGroup.querySelector('.room-info__swatch-value');
                if (!display) return;
                const isMatch = !!typeGroup.querySelector('.room-info__swatch-input:checked');
                display.toggleAttribute('hidden', !isMatch);
                if (isMatch) (display.querySelector('[data-swatch-value]') || display).textContent = value;
            });
            return;
        }

        const valueDisplay =
            group.querySelector('[data-swatch-value]') || group.querySelector('.room-info__swatch-value');
        if (valueDisplay) valueDisplay.textContent = value;
    }

    /** @param {Event} event */
    #handleVariantChange = (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (!input) return;

        if (this.#isMultiOption()) {
            const group = input.closest('[data-option-position]');
            if (!(group instanceof HTMLElement)) return;
            const variant = this.#resolveVariant(Number(group.dataset.optionPosition) - 1, input.value);
            if (!variant) return;
            this.#syncOptionGroups(variant);
            this.#applySelection(this.#selectionFromVariant(variant));
            return;
        }

        const variantType = input.dataset.variantType;
        if (variantType !== undefined) {
            this.querySelectorAll('.room-info__custom-swatch-group').forEach((group) => {
                const valueDisplay = group.querySelector('.room-info__swatch-value');
                if (!valueDisplay) return;
                const isMatch = !!group.querySelector(`input[data-variant-type="${variantType}"]`);
                valueDisplay.toggleAttribute('hidden', !isMatch);
                if (isMatch) valueDisplay.textContent = input.value;
            });
        } else {
            this.#updateValueDisplay(input.closest('.room-info__swatch-group') || this, input.value);
        }

        this.#applySelection({
            variantId: input.dataset.variantId || '',
            price: parseInt(input.dataset.variantPrice || '0', 10) || 0,
            comparePrice: parseInt(input.dataset.variantComparePrice || '0', 10) || 0,
            image: input.dataset.variantImage || '',
            metaImage: input.dataset.variantMetaImage || '',
        });
    };

    /** @param {VariantSelection} selection */
    #applySelection({ variantId, price, comparePrice, image, metaImage }) {
        this.#updateProductImage(image, metaImage);
        this.#updateStockStatus(variantId);

        const productCard = this.closest('.room-info__product');
        if (productCard instanceof HTMLElement) {
            productCard.dataset.selectedVariant = variantId;

            this.dispatchEvent(
                new CustomEvent('room:variant-change', {
                    detail: {
                        productId: productCard.dataset.productId,
                        variantId,
                        price,
                        comparePrice,
                    },
                    bubbles: true,
                })
            );
        }
    }

    /**
     * @param {string} variantImage
     * @param {string} variantMetaImage
     */
    #updateProductImage(variantImage, variantMetaImage) {
        const productCard = this.closest('.room-info__product');
        if (!productCard) return;

        const imageElement = productCard.querySelector('.room-info__product-image');
        if (!(imageElement instanceof HTMLImageElement)) return;

        const hasValidMetaImage = variantMetaImage && !variantMetaImage.includes('no-image-');
        const imageToUse = hasValidMetaImage ? variantMetaImage : variantImage;

        if (imageToUse) {
            imageElement.src = imageToUse;
            imageElement.srcset = '';
        }
    }

    /** @param {string} variantId */
    #updateStockStatus(variantId) {
        const productCard = this.closest('.room-info__product');
        if (!productCard) return;

        productCard.querySelectorAll('.room-info__stock-status').forEach((status) => {
            if (!(status instanceof HTMLElement)) return;
            status.classList.toggle('room-info__stock-status--hidden', status.dataset.variantId !== variantId);
        });
    }
}

if (!customElements.get('room-variant-selector-component')) {
    customElements.define('room-variant-selector-component', RoomVariantSelectorComponent);
}
